// Accessories Carousel Drag Functionality
class AccessoriesCarousel {
  constructor() {
    this.carousel = document.querySelector('.accessories-carousel');
    this.track = document.querySelector('.accessories-carousel-track');
    this.prevButtons = document.querySelectorAll('[data-carousel-prev]');
    this.nextButtons = document.querySelectorAll('[data-carousel-next]');
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationId = 0;
    this.currentIndex = 0;
    this.isInitialized = false;
    this.cardWidth = 300; // Width of each card + gap
    this.visibleCards = 1;

    if (this.carousel && this.track) {
      this.init();
    } else {
      console.warn('Accessories carousel elements not found:', {
        carousel: !!this.carousel,
        track: !!this.track
      });
    }
  }

  init() {
    try {
      // Wait for images to load before calculating dimensions
      this.waitForImagesAndInit();
      
      // Mouse events
      this.carousel.addEventListener('mousedown', this.dragStart.bind(this));
      this.carousel.addEventListener('mouseup', this.dragEnd.bind(this));
      this.carousel.addEventListener('mouseleave', this.dragEnd.bind(this));
      this.carousel.addEventListener('mousemove', this.dragMove.bind(this));

      // Touch events
      this.carousel.addEventListener('touchstart', this.dragStart.bind(this), { passive: false });
      this.carousel.addEventListener('touchend', this.dragEnd.bind(this));
      this.carousel.addEventListener('touchmove', this.dragMove.bind(this), { passive: false });

      // Prevent context menu
      this.carousel.addEventListener('contextmenu', (e) => e.preventDefault());

      // Prevent image dragging
      this.track.querySelectorAll('img').forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Disable text selection during drag
      this.carousel.style.userSelect = 'none';
      this.carousel.style.webkitUserSelect = 'none';

      // Handle window resize
      this.handleResize = this.debounce(() => {
        this.updateDimensions();
        this.updateArrowStates();
      }, 250);
      
      window.addEventListener('resize', this.handleResize);
      
      // Add arrow button event listeners
      this.initArrowButtons();
      
      this.isInitialized = true;
      
    } catch (error) {
      console.error('Error initializing accessories carousel:', error);
    }
  }

  waitForImagesAndInit() {
    const images = this.track.querySelectorAll('img');
    if (images.length === 0) {
      this.updateDimensions();
      return;
    }

    let loadedImages = 0;
    const totalImages = images.length;

    const onImageLoad = () => {
      loadedImages++;
      if (loadedImages === totalImages) {
        setTimeout(() => this.updateDimensions(), 100);
      }
    };

    images.forEach(img => {
      if (img.complete) {
        onImageLoad();
      } else {
        img.addEventListener('load', onImageLoad);
        img.addEventListener('error', onImageLoad);
      }
    });

    // Fallback timeout in case images don't load
    setTimeout(() => {
      if (loadedImages < totalImages) {
        console.warn('Some carousel images failed to load, proceeding anyway');
        this.updateDimensions();
      }
    }, 3000);
  }

  updateDimensions() {
    if (!this.carousel || !this.track) return;
    
    // Force recalculation of dimensions
    this.carousel.style.width = '';
    this.track.style.width = '';
    
    // Trigger reflow
    this.carousel.offsetWidth;
    this.track.scrollWidth;
    
    // Calculate visible cards and card width
    const carouselWidth = this.carousel.offsetWidth;
    const cards = this.track.querySelectorAll('.accessory-card');
    if (cards.length > 0) {
      const trackStyle = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.gap || '24px'); // 1.5rem = 24px
      const cardWidth = cards[0].offsetWidth;
      this.cardWidth = cardWidth + gap;
      this.visibleCards = Math.floor((carouselWidth + gap) / this.cardWidth);
    }
    
  }

  initArrowButtons() {
    // Add event listeners to arrow buttons
    this.prevButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToPrevious();
      });
    });

    this.nextButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToNext();
      });
    });

    // Initial arrow state update
    setTimeout(() => this.updateArrowStates(), 100);
  }

  goToPrevious() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.goToSlide(this.currentIndex);
    }
  }

  goToNext() {
    const cards = this.track.querySelectorAll('.accessory-card');
    const maxIndex = Math.max(0, cards.length - this.visibleCards);
    
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.goToSlide(this.currentIndex);
    }
  }

  goToSlide(index) {
    const translateX = -index * this.cardWidth;
    this.currentTranslate = translateX;
    this.prevTranslate = translateX;
    this.setSliderPosition();
    this.updateArrowStates();
  }

  updateArrowStates() {
    const cards = this.track.querySelectorAll('.accessory-card');
    const maxIndex = Math.max(0, cards.length - this.visibleCards);
    
    // Update previous buttons
    this.prevButtons.forEach(button => {
      if (this.currentIndex <= 0) {
        button.classList.add('accessories-arrow--disabled');
        button.disabled = true;
      } else {
        button.classList.remove('accessories-arrow--disabled');
        button.disabled = false;
      }
    });
    
    // Update next buttons
    this.nextButtons.forEach(button => {
      if (this.currentIndex >= maxIndex) {
        button.classList.add('accessories-arrow--disabled');
        button.disabled = true;
      } else {
        button.classList.remove('accessories-arrow--disabled');
        button.disabled = false;
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  getPositionX(event) {
    if (event.type.includes('mouse')) {
      return event.clientX;
    } else if (event.touches && event.touches.length > 0) {
      return event.touches[0].clientX;
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }
    console.warn('Unable to get position from event:', event);
    return 0;
  }

  dragStart(event) {
    const target = event.target;
    
    // Ignore drag start if clicking on interactive elements
    if (target.closest('button') || target.closest('a') || target.closest('modal-opener')) {
      return;
    }
    
    // Only allow drag start if clicking on an accessory card
    if (!target.closest('.accessory-card')) {
      return;
    }
    
    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.carousel.style.cursor = 'grabbing';
    this.track.style.transition = 'none';
    
    // Cancel any ongoing animation
    cancelAnimationFrame(this.animationId);
  }

  dragMove(event) {
    if (!this.isDragging) return;
    
    event.preventDefault();
    const currentPosition = this.getPositionX(event);
    
    if (currentPosition === 0) return; // Skip if we couldn't get position
    
    this.currentTranslate = this.prevTranslate + currentPosition - this.startPos;
    
    // Add resistance at boundaries with safe dimension checks
    const maxTranslate = 0;
    const carouselWidth = this.carousel.offsetWidth || 0;
    const trackWidth = this.track.scrollWidth || 0;
    const minTranslate = trackWidth > carouselWidth ? -(trackWidth - carouselWidth) : 0;
    
    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate + (this.currentTranslate - maxTranslate) * 0.2;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate + (this.currentTranslate - minTranslate) * 0.2;
    }
    
    this.setSliderPosition();
  }

  dragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.carousel.style.cursor = 'grab';
    this.track.style.transition = 'transform 0.3s ease-out';
    
    // Snap to boundaries if needed with safe dimension checks
    const maxTranslate = 0;
    const carouselWidth = this.carousel.offsetWidth || 0;
    const trackWidth = this.track.scrollWidth || 0;
    const minTranslate = trackWidth > carouselWidth ? -(trackWidth - carouselWidth) : 0;
    
    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate;
    }
    
    // Update current index based on translate position
    if (this.cardWidth > 0) {
      this.currentIndex = Math.round(Math.abs(this.currentTranslate) / this.cardWidth);
    }
    
    this.prevTranslate = this.currentTranslate;
    this.setSliderPosition();
    this.updateArrowStates();
  }

  setSliderPosition() {
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  // Cleanup method to prevent memory leaks
  destroy() {
    if (!this.isInitialized) return;
    
    try {
      // Remove event listeners
      if (this.carousel) {
        this.carousel.removeEventListener('mousedown', this.dragStart);
        this.carousel.removeEventListener('mouseup', this.dragEnd);
        this.carousel.removeEventListener('mouseleave', this.dragEnd);
        this.carousel.removeEventListener('mousemove', this.dragMove);
        this.carousel.removeEventListener('touchstart', this.dragStart);
        this.carousel.removeEventListener('touchend', this.dragEnd);
        this.carousel.removeEventListener('touchmove', this.dragMove);
        this.carousel.removeEventListener('contextmenu', (e) => e.preventDefault());
      }
      
      // Remove arrow button event listeners
      this.prevButtons.forEach(button => {
        button.removeEventListener('click', this.goToPrevious);
      });
      
      this.nextButtons.forEach(button => {
        button.removeEventListener('click', this.goToNext);
      });
      
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
      
      // Cancel any ongoing animations
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      
      // Reset styles
      if (this.track) {
        this.track.style.transform = '';
        this.track.style.transition = '';
      }
      
      if (this.carousel) {
        this.carousel.style.cursor = '';
      }
      
      this.isInitialized = false;
      
    } catch (error) {
      console.error('Error during carousel cleanup:', error);
    }
  }
}

// Enhanced accordion functionality
document.addEventListener('DOMContentLoaded', function() {
  // Initialize accordion
  const accordionItems = document.querySelectorAll('.product-accordion .accordion-item');
  
  accordionItems.forEach(item => {
    const summary = item.querySelector('summary');
    
    summary.addEventListener('click', function(e) {
      // Optional: Close other accordion items for better UX
      // accordionItems.forEach(otherItem => {
      //   if (otherItem !== item && otherItem.open) {
      //     otherItem.open = false;
      //   }
      // });
    });
  });

  // Initialize carousel with retry mechanism
  initializeCarouselWithRetry();
});

function initializeCarouselWithRetry() {
  let attempts = 0;
  const maxAttempts = 5;
  const retryDelay = 500;

  function tryInitialize() {
    attempts++;
    
    const carousel = document.querySelector('.accessories-carousel');
    const track = document.querySelector('.accessories-carousel-track');
    
    if (carousel && track) {
      new AccessoriesCarousel();
      return;
    }
    
    if (attempts < maxAttempts) {
      setTimeout(tryInitialize, retryDelay);
    } else {
      console.warn('Failed to initialize carousel after maximum attempts');
    }
  }
  
  tryInitialize();
}

// Also observe for dynamic content changes
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if carousel elements were added
            if (node.classList && (node.classList.contains('accessories-carousel') || 
                node.querySelector && node.querySelector('.accessories-carousel'))) {
              setTimeout(() => new AccessoriesCarousel(), 100);
            }
          }
        });
      }
    });
  });
  
  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
